<template>
  <form>
    <div class="image-container mb-3" v-if="previewPath">
      <img :src="previewPath" alt="Uploaded Image Preview" />
    </div>
    <div class="form-group">
      <div ref="dashboardContainer"></div>
    </div>
  </form>
</template>

<script>
import Uppy from "@uppy/core";

import Dashboard from "@uppy/dashboard";
import Russian from "@uppy/locales/lib/ru_RU";
import English from "@uppy/locales/lib/en_US";
import AwsS3 from "@uppy/aws-s3";
// import AwsS3Multipart from "@uppy/aws-s3-multipart";
// import XHRUpload from "@uppy/xhr-upload";
import Compressor from "@uppy/compressor";
// import Webcam from '@uppy/webcam';
import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import '@uppy/webcam/dist/style.min.css';

export default {
  props: {
    maxFileSizeInBytes: {
      type: Number,
      required: true,
    },
    width: {
      type: Number,
      required: true,
    },
    height: {
      type: Number,
      required: true,
    },
    booking_id: {
      type: Number,
      required: true,
    },
    theme: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      payload: null,
      previewPath: null,
      disabled: true,
      uploadedPhotos: [],
    };
  },
  mounted() {
    this.instantiateUppy();
    console.log(process.env.NODE_ENV)
  },
  beforeDestroy() {
    this.uppy.close();
  },
  methods: {
    instantiateUppy() {
      let self = this;
      this.uppy = new Uppy({
        debug: false,
        locale: this.$i18n.locale === "ru" ? Russian : English,
        autoProceed: true,
        allowMultipleUploads: true,
        restrictions: {
          maxFileSize: this.maxFileSizeInBytes,
          minNumberOfFiles: 1,
          maxNumberOfFiles: 15,
          allowedFileTypes: ["image/*", ".heic", ".heif", "video/*", ".mov"],
        }
      })
      .use(Compressor, {
        quality: 0.7,
        limit: 5,
        mimeTypes: ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"],
        width: 1920,
        height: 1080,
        convertSize: 5000000,
        convertTypes: ["image/png"],
        preserveExif: false,
        locale: {
          strings: {
            // Shown in the Status Bar
            compressingImages: this.$i18n.locale === "ru" ? 'Сжимаем изображение...' : 'Compressing image...',
            compressedX: this.$i18n.locale === "ru" ? 'Сжато %{size} байт' : 'Compressed %{size} bytes',
          },
        },
      })
      .use(Dashboard, {
        hideUploadButton: true,
        theme: this.theme,
        inline: true,
        width: this.width,
        height: this.height,
        target: this.$refs.dashboardContainer,
        replaceTargetContent: true,
        showProgressDetails: true,
        browserBackButtonClose: true,
        locale: {
          strings: {
            poweredBy2: "",
          },
        }
      })
      .use(AwsS3, {
        companionUrl: '', // Не используем companion, будем получать presigned URL напрямую
        limit: 3,               // Максимум 3 параллельных загрузки
        timeout: 60000,
        getUploadParameters: async (file) => {
          // Запрашиваем presigned URL для каждого файла
          try {
            const response = await fetch(
              process.env.NODE_ENV === "development"
                ? `http://localhost:3000/api/v1/booking_photos/${this.booking_id}/presigned_url`
                : `https://rentprog.net/api/v1/booking_photos/${this.booking_id}/presigned_url`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': this.$store.getters.getAuthToken
                },
                body: JSON.stringify({
                  filename: file.name,
                  content_type: file.type,
                  size: file.size,
                  checkin: true
                })
              }
            );

            const data = await response.json();

            if (!response.ok) {
              console.error('Error getting presigned URL:', data);
              throw new Error(data.error || 'Failed to get presigned URL');
            }

            console.log('Received presigned URL data:', data);

            // Сохраняем ID фото и ключ для последующего подтверждения
            file.meta = file.meta || {};
            file.meta.booking_photo_id = data.booking_photo_id;
            file.meta.key = data.key;

            // Возвращаем параметры для загрузки в S3
            return {
              method: 'POST',
              url: data.presigned_url,
              fields: data.fields,
              fieldName: 'file'
            };
          } catch (error) {
            console.error('Error in getUploadParameters:', error);
            throw error;
          }
        }
      });
      // .use(XHRUpload, {
      //   endpoint: process.env.NODE_ENV === "development" ?
      //     `http://localhost:3000/api/v1/booking_photos/${this.booking_id}/` :
      //     `https://rentprog.net/api/v1/booking_photos/${this.booking_id}/`,
      //   method: "post",
      //   formData: true,
      //   fieldName: "images[]",
      //   bundle: false,
      //   headers: {
      //     Authorization: this.$store.getters.getAuthToken,
      //   },
      // });

      this.uppy.on('error', (error) => {
        console.error('Error file upload:', error);
      });

      this.uppy.on('upload-error', (file, error, response) => {
        console.error('Upload error:', {
          file: file.name,
          error: error.message || error,
          response: response || 'No response',
          meta: file.meta || 'No metadata'
        });
      });

      // Добавим новые события для отслеживания этапов
      this.uppy.on('file-added', (file) => {
        console.log('File added:', file.name, file.size);
      });

      this.uppy.on('upload-progress', (file, progress) => {
        const progressPercentage = Math.floor((progress.bytesUploaded / progress.bytesTotal) * 100);
        console.log(`Upload progress: ${file.name} - ${progressPercentage}%`);
      });

      // Отслеживание этапов компрессии, если включен Compressor
      this.uppy.on('preprocess-progress', (file, progress) => {
        console.log(`Compression progress for ${file.name}: ${Math.round(progress * 100)}%`);
      });

      this.uppy.on('preprocess-complete', (file) => {
        console.log(`Compression complete for ${file.name}`);
        if (file.meta.compressorOriginalSize) {
          const ratio = Math.round((1 - file.size / file.meta.compressorOriginalSize) * 100);
          console.log(`Reduced by ${ratio}%`);
        }
      });

      this.uppy.on('upload', () => {
        console.log('Upload started');
        this.uploadedPhotos = []; // Сбрасываем список загруженных фото
      });

      this.uppy.on('complete', (result) => {
        console.log('Upload complete:', result);

        if (result.successful && result.successful.length > 0) {
          // Если есть файлы, которые мы уже обработали в upload-success
          if (this.uploadedPhotos && this.uploadedPhotos.length > 0) {
            this.$emit('updateCheckinPhotos', this.uploadedPhotos);
            return;
          }

          // Резервный вариант, если upload-success не сработал
          const uploadedPhotos = result.successful.map(file => {
            let responseData = {};
            try {
              if (file.response && file.response.body) {
                responseData = typeof file.response.body === 'string'
                  ? JSON.parse(file.response.body)
                  : file.response.body;
              }
            } catch (e) {
              console.error('Error parsing response:', e);
              console.log('Response body:', file.response ? file.response.body : 'no response body');
            }

            return {
              id: responseData.id || file.id,
              url: responseData.url || file.uploadURL || file.preview,
              small_url: responseData.small_url || file.uploadURL || file.preview,
              position: responseData.position || 0,
              content_type: file.type,
              checkin: true
            };
          });

          this.$emit('updateCheckinPhotos', uploadedPhotos);
        } else if (result.failed && result.failed.length > 0) {
          console.warn('Some files not uploaded', result.failed);
        }
      });

      this.uppy.on('upload-success', (file, response) => {
        // В Uppy 2.x метаданные всегда должны существовать в file.meta
        // Если их нет, инициализируем
        if (!file.meta) file.meta = {};

        if (!file.meta.booking_photo_id || !file.meta.key) {
          console.error('Missing metadata:', file.meta);
          this.uppy.setFileState(file.id, {
            error: 'Missing required metadata for confirmation'
          });
          return;
        }

        console.log('Upload success - sending confirmation to backend:', {
          id: file.meta.booking_photo_id,
          key: file.meta.key,
          filename: file.name,
          size: file.size,
          content_type: file.type
        });

        // В Uppy 2.x структура response уже содержит .body
        fetch(
          process.env.NODE_ENV === "development"
            ? `http://localhost:3000/api/v1/booking_photos/${file.meta.booking_photo_id}/confirm_upload`
            : `https://rentprog.net/api/v1/booking_photos/${file.meta.booking_photo_id}/confirm_upload`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': this.$store.getters.getAuthToken
            },
            body: JSON.stringify({
              key: file.meta.key,
              filename: file.name,
              size: file.size,
              content_type: file.type
            })
          }
        )
        .then(res => {
          if (!res.ok) {
            return res.json().then(errData => {
              console.error('Error confirmation response:', errData);
              throw new Error(`Failed to confirm upload: ${res.status} - ${JSON.stringify(errData)}`);
            });
          }
          return res.json();
        })
        .then(data => {
          console.log('Confirmation successful, wait processing, received data:', data);

          // // Проверка наличия URL
          // if (!data.url) {
          //   console.error('No URL in confirmation response:', data);
          //   throw new Error('No URL in confirmation response');
          // }

          // // Инициализируем массив, если он не существует
          // if (!this.uploadedPhotos) this.uploadedPhotos = [];

          // // Добавляем фото в список загруженных
          // this.uploadedPhotos.push(data);

          // // В Uppy 2.x getFiles() может работать иначе, проверяем успешно загруженные файлы
          // const uploadedCount = this.uppy.getFiles().filter(f =>
          //   f.progress && f.progress.uploadComplete).length;

          // console.log(`Uploaded ${this.uploadedPhotos.length} of ${uploadedCount} files`);

          // // Если все файлы загружены, передаем родительскому компоненту
          // if (this.uploadedPhotos.length === uploadedCount) {
          //   console.log('All files uploaded, emitting event with:', this.uploadedPhotos);
          //   this.$emit('updateCheckinPhotos', this.uploadedPhotos);
          // }
        })
        .catch(err => {
          console.error('Error confirming upload:', err);
          // В Uppy 2.x можно пометить файл как ошибочный
          this.uppy.setFileState(file.id, {
            error: `Confirmation failed: ${err.message}`
          });
        });
      });

      // Добавляем обработчик ошибок ограничения для лучшей обратной связи
      this.uppy.on('restriction-failed', (file, error) => {
        let message = '';

        switch(error.reason) {
          case 'maxFileSize':
            message = `File too large: ${file.name}. Maximum size is ${this.formatFileSize(this.maxFileSizeInBytes)}`;
            break;
          case 'minNumberOfFiles':
            message = 'Please select at least one file';
            break;
          case 'maxNumberOfFiles':
            message = 'Too many files selected';
            break;
          case 'allowedFileTypes':
            message = `File type not allowed: ${file.name}. Allowed types: images, videos, HEIC/HEIF`;
            break;
          default:
            message = error.message || 'File rejected';
        }

        console.warn(message);
        // Можно показать пользователю сообщение, если нужно
      });
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
  },
};
</script>

<style scoped>
.image-container {
  height: 150px;
  width: 150px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: auto;
  margin-left: auto;
}

.image-container>img {
  width: inherit;
  height: inherit;
}
</style>
